import { HttpEvent, HttpEventType } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiResponse } from '@core/interface/api-response.interface';
import { PageableResponse } from '@core/interface/pageable.interface';
import { PurchaseFileService } from '@core/services/entities/purchase-file.service';
import { ConfirmPurchaseFileUploadRequestDto } from '@domain/purchase-file/dtos/confirm-purchase-file-upload.request.dto';
import { CreatePurchaseFileUploadSasRequestDto } from '@domain/purchase-file/dtos/create-purchase-file-upload-sas.request.dto';
import { ListPurchaseFileRequestDto } from '@domain/purchase-file/dtos/list-purchase-file.request.dto';
import { PurchaseFileUploadSasResponseDto } from '@domain/purchase-file/dtos/purchase-file-upload-sas.response.dto';
import { PurchaseFileResponseDto } from '@domain/purchase-file/dtos/purchase-file.response.dto';
import { UploadPurchaseFileToBlobStorageRequestDto } from '@domain/purchase-file/dtos/upload-purchase-file-to-blob-storage.request.dto';
import { PurchaseFileUploadProgressModel } from '@domain/purchase-file/models/purchase-file-upload-progress.model';
import { PurchaseFileRepository } from '@domain/purchase-file/repositories/purchase-file.repository';
import { Observable, filter, map } from 'rxjs';

/**
 * The upload PUT reports bytes sent, but the file is only safely stored once
 * blob storage answers. Streaming progress is therefore capped just below 100%
 * so the UI never claims completion while the response is still in flight.
 */
const MAX_STREAMING_PROGRESS_PERCENTAGE = 99;

@Injectable({
  providedIn: 'root',
})
export class PurchaseFileRepositoryService implements PurchaseFileRepository {
  constructor(
    private purchaseFileService: PurchaseFileService,
  ) {}

  createUploadSas(
    request: CreatePurchaseFileUploadSasRequestDto,
  ): Observable<ApiResponse<PurchaseFileUploadSasResponseDto>> {
    return this.purchaseFileService.createUploadSas(request);
  }

  uploadToBlobStorage(
    request: UploadPurchaseFileToBlobStorageRequestDto,
  ): Observable<PurchaseFileUploadProgressModel> {
    return this.purchaseFileService
      .uploadToBlobStorage(
        request.uploadUrl,
        request.file,
        request.requiredHeaders,
      )
      .pipe(
        map((httpEvent: HttpEvent<void>) =>
          this.mapHttpEventToProgress(httpEvent, request.file.size),
        ),
        filter(
          (progress): progress is PurchaseFileUploadProgressModel =>
            progress !== null,
        ),
      );
  }

  confirmUpload(
    request: ConfirmPurchaseFileUploadRequestDto,
  ): Observable<ApiResponse<PurchaseFileResponseDto>> {
    return this.purchaseFileService.confirmUpload(request);
  }

  list(
    request: ListPurchaseFileRequestDto,
  ): Observable<
    ApiResponse<PageableResponse<PurchaseFileResponseDto>>
  > {
    return this.purchaseFileService.list(request);
  }

  reconcile(
    id: string,
  ): Observable<ApiResponse<PurchaseFileResponseDto>> {
    return this.purchaseFileService.reconcile(id);
  }

  downloadErrors(id: string): Observable<ApiResponse<{ viewUrl: string }>> {
    return this.purchaseFileService.downloadErrors(id);
  }

  downloadFile(id: string): Observable<ApiResponse<{ viewUrl: string }>> {
    return this.purchaseFileService.downloadFile(id);
  }

  private mapHttpEventToProgress(
    httpEvent: HttpEvent<void>,
    fileSizeBytes: number,
  ): PurchaseFileUploadProgressModel | null {
    if (httpEvent.type === HttpEventType.UploadProgress) {
      const totalBytes = httpEvent.total ?? fileSizeBytes;
      const rawPercentage =
        totalBytes > 0 ? Math.round((httpEvent.loaded / totalBytes) * 100) : 0;

      return new PurchaseFileUploadProgressModel({
        uploadProgressPercentage: Math.min(
          rawPercentage,
          MAX_STREAMING_PROGRESS_PERCENTAGE,
        ),
        isCompleted: false,
      });
    }

    if (httpEvent.type === HttpEventType.Response) {
      return new PurchaseFileUploadProgressModel({
        uploadProgressPercentage: 100,
        isCompleted: true,
      });
    }

    return null;
  }
}
